import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@auth0/auth0-angular';
import {
  WHDomEvent,
  WHLayoutConfigService,
  WHLayoutConfigDOM,
  WHLayoutLanguageDOM,
  WHLayoutLoggedInUserDOM,
  WHLayoutLogoDOM,
  WHLayoutNavItemDOM,
  WHLayoutNavItemTypeENUM,
  WHThemeConfigService,
  WHLoginDataService,
  WHPermissionKeyENUM,
  MGMT_GATEWAY,
  WHFeatureKeyENUM,
  WHTeamDataService,
  IWHTeamDTO,
  WHTeamDOM,
} from '@workheld/workheld-shared-lib';
import { of, firstValueFrom, Subscription } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { helpNavDOM, quickNavDOM } from './app.config';
import { UIService } from './app-services/ui.service';
import { AppDialogService } from './app-dialogs/app-dialog.service';

@Component({
  selector: 'app-layout',
  templateUrl: './app-layout.component.html',
  standalone: false,
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  public quickNavItemDOM: WHLayoutNavItemDOM = quickNavDOM;
  public helpNavItemDOM: WHLayoutNavItemDOM = helpNavDOM;

  // VAR
  public permissions: string[] = [];

  // LAYOUT VAR
  public layoutConfigDOM: WHLayoutConfigDOM;
  public layoutNavItemDOMList: WHLayoutNavItemDOM[] = [];
  public layoutLanguageDOMList: WHLayoutLanguageDOM[];
  public layoutLoggedInUserDOM: WHLayoutLoggedInUserDOM;
  public layoutLoggedInUserNavItemDOMList: WHLayoutNavItemDOM[] = [];
  public layoutLogoDOM: WHLayoutLogoDOM;

  // SUBSCRIPTION
  private readonly subscription: Subscription = new Subscription();
  private readonly modalActionMap: Record<
    number,
    () => MatDialogRef<unknown, unknown>
  > = {
    201: () => this.appDialogService.createProjectDialog(),
  };
  isWhSenseEnabled: boolean;
  isSmallScreen = signal(false);
  layoutMobileNavItemDOMList = [];
  isAiFeatureEnabled = signal(false);

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly layoutConfigService: WHLayoutConfigService,
    private readonly themeConfigService: WHThemeConfigService,
    private readonly translateService: TranslateService,
    private readonly loginDataService: WHLoginDataService,
    private readonly teamDataService: WHTeamDataService,
    private readonly uiService: UIService,
    private readonly appDialogService: AppDialogService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngOnInit(): void {
    this.isSmallScreen = this.uiService.$isSmallScreen;

    this.subscription.add(
      this.loginDataService.featureConfigMap$
        .pipe(
          map((features: Map<string, boolean>) => {
            this.isWhSenseEnabled = features.get(
              WHFeatureKeyENUM.QUICKNAV_WHSENSE,
            );

            this.isAiFeatureEnabled.set(
              features.get(WHFeatureKeyENUM.WORKHELD_AI),
            );

            this.quickNavItemDOM.children.find(
              (c) => c.translateKey === 'app.whsense.label',
            ).enabled = this.isWhSenseEnabled;
          }),
        )
        .subscribe(),
    );

    this.subscription.add(
      this.loginDataService.permissions$.subscribe((permissions: string[]) => {
        this.permissions = permissions;
      }),
    );

    this.subscription.add(
      this.teamDataService
        .getTeamDTOList({
          apiUrl: environment.apiUrl + MGMT_GATEWAY,
        })
        .subscribe((teams: IWHTeamDTO[]) => {
          const teamDOMList: WHTeamDOM[] = [];
          teams
            .filter((t) => t.status !== 'INACTIVE')
            .sort((a: IWHTeamDTO, b: IWHTeamDTO) => {
              if (a.name.toLowerCase() < b.name.toLowerCase()) {
                return -1;
              }
              if (a.name.toLowerCase() > b.name.toLowerCase()) {
                return 1;
              }
              return 0;
            })
            .forEach((teamDTO: IWHTeamDTO) => {
              teamDOMList.push(new WHTeamDOM(teamDTO));
            });
          this.teamDataService.teams$.next(teamDOMList);
        }),
    );

    // LAYOUT CONFIG
    this.subscription.add(
      this.layoutConfigService.layoutConfigDOM$.subscribe(
        (layoutConfigDOM: WHLayoutConfigDOM) => {
          this.layoutConfigDOM = layoutConfigDOM;
        },
      ),
    );

    // NAVIGATION ITEMS
    this.subscription.add(
      this.layoutConfigService.layoutNavItemDOMList$.subscribe(
        (layoutNavItemDOMList: WHLayoutNavItemDOM[]) => {
          if (this.coordinatorAccess) {
            this.assignModalActions(layoutNavItemDOMList);
            this.layoutNavItemDOMList = layoutNavItemDOMList;
            this.layoutMobileNavItemDOMList = this.mobileNavItems();
          }
        },
      ),
    );

    // LANGUAGE
    this.subscription.add(
      this.layoutConfigService.layoutLanguageDOMList$.subscribe(
        (layoutLanguageDOMList: WHLayoutLanguageDOM[]) => {
          this.layoutLanguageDOMList = layoutLanguageDOMList;
        },
      ),
    );

    // LOGGED IN USER
    this.subscription.add(
      this.layoutConfigService.layoutLoggedInUserDOM$.subscribe(
        (layoutLoggedInUserDOM: WHLayoutLoggedInUserDOM) => {
          this.layoutLoggedInUserDOM = layoutLoggedInUserDOM;
        },
      ),
    );

    // LOGGED IN USER NAV ITEMS
    this.subscription.add(
      this.layoutConfigService.layoutLoggedInUserNavItemDOMList$.subscribe(
        (layoutLoggedInUserNavItemDOMList: WHLayoutNavItemDOM[]) => {
          this.layoutLoggedInUserNavItemDOMList =
            layoutLoggedInUserNavItemDOMList;
        },
      ),
    );

    // LOGO
    this.subscription.add(
      this.layoutConfigService.layoutLogoDOM$.subscribe(
        (layoutLogoDOM: WHLayoutLogoDOM) => {
          this.layoutLogoDOM = layoutLogoDOM;
        },
      ),
    );
  }

  public get coordinatorAccess() {
    return this.permissions.includes(
      WHPermissionKeyENUM.FLOW_COORDINATOR_ACCESS,
    );
  }

  public async onDomChildEvent($event: WHDomEvent): Promise<void> {
    switch ($event.type) {
      case 'logoClick':
        this.router.navigate(['/']);
        return;
      case 'quickItemClick':
        if (($event.payload as WHLayoutNavItemDOM).routeUrl) {
          window.open(($event.payload as WHLayoutNavItemDOM).routeUrl, '_self');
        }
        return;
      case 'navigate':
        // Navigation logic removed with WHRouteBehaviorSubjectService
        return;
      case 'languageChange':
        localStorage.setItem('whlang', $event.payload.key);
        this.translateService.setFallbackLang($event.payload.key);
        return;
      case 'toggle':
        if (
          ($event.payload as WHLayoutNavItemDOM).featureKey ===
          'WHFeatureKeyENUM.DARK'
        ) {
          this.themeConfigService.dark$.next(
            ($event.payload as WHLayoutNavItemDOM).checked,
          );
        }
        return;
      case WHLayoutNavItemTypeENUM.MODAL: {
        const navItem = $event.payload as WHLayoutNavItemDOM;
        if (navItem.modalAction) {
          navItem.modalAction();
        }
        return;
      }
      case 'logout':
        await this.logout();
        return;
      default:
        return;
    }
  }

  private loading(loading: boolean): any {
    return this.authService.isLoading$.pipe(
      switchMap((innerLoading: boolean) => {
        if (loading) {
          return this.loading(innerLoading);
        }
        return of(innerLoading);
      }),
      take(1),
    );
  }

  private async logout(): Promise<void> {
    const confirm = await firstValueFrom(
      this.appDialogService
        .openConfirmMatDialog({
          header: 'nav.userprofile.logout',
          body: 'confirmationdialog.shiftmodeldelete.title.label',
          submitAction: 'nav.userprofile.logout',
          submitIcon: 'logout',
        })
        .afterClosed(),
    );

    if (!confirm) {
      return;
    }
    localStorage.removeItem('wh_translations_last_updated');
    sessionStorage.removeItem('wh-ai-session');
    this.authService.logout({
      logoutParams: {
        returnTo: this.document.location.origin,
        client_id: environment.auth0Config.clientId,
        federated: true,
      },
    });
  }

  public mobileNavItems() {
    const items = this.layoutNavItemDOMList.slice(0, 6).map((item) => ({
      ...item,
      showParent: item.children.some((child) => child.enabled),
    }));

    const menuItems = [{ translateKey: '', children: items }];

    return menuItems;
  }

  private assignModalActions(navItems: WHLayoutNavItemDOM[]): void {
    for (const item of navItems) {
      if (item.type === WHLayoutNavItemTypeENUM.MODAL && item.id != null) {
        const action = this.modalActionMap[item.id];
        if (action) {
          item.modalAction = action;
        }
      }
      if (item.children?.length) {
        this.assignModalActions(item.children);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
